/**
 * FounderOpening — replaces the multi-frame cinematic with the Lead's
 * personal story (Apr 27 2026). Single hero shot:
 *   1. Tap to begin
 *   2. Black hold → Earth fades in (Moon removed Apr 30 2026)
 *   3. Founder narration plays sequentially (silent captions until the
 *      new ElevenLabs voice clone is baked, then real audio)
 *   4. Closing: clean welcome line + "Press the globe to continue"
 *      (Share constellation + text-shadow halo removed Apr 30 2026
 *      per Lead — page must match the clean baseline.)
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SkipForward, Volume2, VolumeX } from 'lucide-react';
import api from '../lib/api';
import absoluteMediaUrl from '../lib/absoluteMediaUrl';
import PopulatedGlobe from './PopulatedGlobe';

const STORAGE_KEY_SEEN = 'tsogn_cinematic_seen_v2';

export function hasSeenCinematic() {
  try { return localStorage.getItem(STORAGE_KEY_SEEN) === 'true'; }
  catch { return false; }
}

export function markCinematicSeen() {
  try { localStorage.setItem(STORAGE_KEY_SEEN, 'true'); }
  catch { /* ignore */ }
}

export function clearCinematicSeen() {
  try { localStorage.removeItem(STORAGE_KEY_SEEN); }
  catch { /* ignore */ }
}

const PHASES = {
  TAP: 'tap',
  PLAYING: 'playing',     // captions running (Earth visible, narration sequential)
  REVEALED: 'revealed',   // closing CTA + share
};

export default function FounderOpening({ onComplete }) {
  const navigate = useNavigate();
  const [manifest, setManifest] = useState(null);
  // Apr 30 2026 iter_63 per Lead: "And I dont see why we have to have the
  // first one to press play. When a user comes to the site it should just
  // play automatically. So remove the first one." The TAP phase is now
  // SKIPPED entirely — we land directly in PLAYING and the narration + Earth
  // fade-in begins immediately. Browsers may block audio autoplay without
  // a prior user gesture, but the on-screen captions advance on their
  // own timer so the cinematic still plays through. Users can still skip
  // (SkipForward button) to jump straight to /world.
  const [phase, setPhase] = useState(PHASES.PLAYING);
  const [lineIndex, setLineIndex] = useState(-1);
  const [showCta, setShowCta] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const timersRef = useRef([]);
  const autoplayStartedRef = useRef(false);

  // Manifest fetch — once on mount.
  useEffect(() => {
    let cancelled = false;
    api.get('/cinematic/manifest')
      .then((r) => { if (!cancelled) setManifest(r.data); })
      .catch(() => { if (!cancelled) setManifest({ founder_opening: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Auto-start playback when ?autoplay=1 lands the user directly here.
  // We can't fire <audio>.play() outside a user gesture, but the carry-over
  // gesture from PublicLanding's Watch-the-intro click satisfies most
  // browsers; if audio is blocked, the captions still advance on their
  // duration timer and the cinematic completes.
  useEffect(() => {
    if (phase !== PHASES.PLAYING) return;
    if (!manifest) return;
    if (autoplayStartedRef.current) return;
    autoplayStartedRef.current = true;
    // Earth fade-in budget then first narration line.
    const t = setTimeout(() => setLineIndex(0), 1800);
    timersRef.current.push(t);
  }, [phase, manifest]);

  // Cleanup timers + audio when component unmounts (one-time, on real unmount).
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      try { audioRef.current?.pause(); } catch {/* ignore */}
    };
  }, []);

  const lines = manifest?.founder_opening || [];
  const currentLine = lineIndex >= 0 && lineIndex < lines.length ? lines[lineIndex] : null;
  const audioReady = !!manifest?.founder_opening_ready;

  // ─── Movie-style single-line subtitle splits ─────────────────────────
  // Apr 27 2026 brief: "just text thats following my voice and fades away.
  // Movie like clean." Each narration line may carry several sentences;
  // we split on terminal punctuation and surface ONE sentence at a time so
  // the screen never stacks more than a single line of caption text.
  const sublines = useMemo(() => {
    if (!currentLine?.text) return [];
    return currentLine.text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [currentLine?.text]);
  const [sublineIdx, setSublineIdx] = useState(0);
  // Reset subline cursor whenever the parent line changes.
  useEffect(() => { setSublineIdx(0); }, [lineIndex]);

  // Apr 30 2026 iter_67 per Lead: "Welcome video does not have narration
  // in sync." Previously subtitles advanced on a pure word-count timer
  // (320ms / word) which drifted from the actual ElevenLabs audio. The
  // audio plays at ElevenLabs's pace, not ours, so a timer-based advance
  // was always off. Now we compute per-subline time WEIGHTS (word count)
  // and, when the audio is known, advance the subline when
  // `audio.currentTime / audio.duration` crosses the cumulative weight
  // of the current subline. When audio is silent / blocked we fall back
  // to the word-count heuristic so the cinematic still completes.
  const sublineWeights = useMemo(() => {
    if (!sublines.length) return [];
    const raw = sublines.map((s) => Math.max(2, (s || '').split(/\s+/).filter(Boolean).length));
    const total = raw.reduce((a, b) => a + b, 0) || 1;
    const cumulative = [];
    let acc = 0;
    for (const w of raw) {
      acc += w / total;
      cumulative.push(acc);
    }
    return cumulative; // array of cumulative fractions [0..1]
  }, [sublines]);

  useEffect(() => {
    if (phase !== PHASES.PLAYING) return;
    if (!sublines.length) return;
    const audio = audioRef.current;
    // AUDIO-DRIVEN path — bind to timeupdate and advance at fractional
    // crossings. If duration isn't known yet we skip and let the
    // fallback timer keep things moving.
    if (audio && !audio.paused) {
      const onTime = () => {
        const dur = audio.duration;
        if (!dur || !isFinite(dur)) return;
        const frac = audio.currentTime / dur;
        // Find the first cumulative weight >= current fraction.
        let target = 0;
        for (let i = 0; i < sublineWeights.length; i++) {
          if (frac >= sublineWeights[i]) target = i + 1;
        }
        // Clamp so we never exceed the last subline index.
        const next = Math.min(sublineWeights.length - 1, target);
        setSublineIdx((cur) => (cur !== next ? next : cur));
      };
      audio.addEventListener('timeupdate', onTime);
      return () => audio.removeEventListener('timeupdate', onTime);
    }
    // FALLBACK — audio not playing. Word-count timer keeps captions moving.
    const text = sublines[sublineIdx] || '';
    const wc = Math.max(2, text.split(/\s+/).length);
    const dur = Math.max(2200, Math.min(7000, wc * 320));
    const t = setTimeout(() => {
      setSublineIdx((i) => (i + 1 < sublines.length ? i + 1 : i));
    }, dur);
    return () => clearTimeout(t);
  }, [phase, sublineIdx, sublines, sublineWeights, lineIndex]);
  const visibleSubline = sublines[sublineIdx] || '';

  // Sequential play — driven by lineIndex changes via useEffect to keep
  // logic dead-simple and StrictMode-safe.
  useEffect(() => {
    if (phase !== PHASES.PLAYING || lineIndex < 0) return;
    if (lineIndex >= lines.length) {
      // Sequence complete.
      const t = setTimeout(() => {
        setPhase(PHASES.REVEALED);
        setTimeout(() => setShowCta(true), 1200);
      }, 600);
      timersRef.current.push(t);
      return () => clearTimeout(t);
    }
    const line = lines[lineIndex];
    const advance = () => setLineIndex((i) => i + 1);
    let timer;
    let audio;
    // Apr 29 2026 follow-up #4: Lead's ElevenLabs voice clone has now
    // baked the new founder script (all 13 lines via /api/cinematic/manifest).
    // Re-enable audio playback. Subtitles stay removed (Lead: "Just the
    // script exactly. Not the old recording.") — the audio carries the
    // narration now. If the audio fails (404 / network), fall through to
    // reading-speed pacing so the cinematic still completes.
    if (line.audio_url && !muted) {
      audio = new Audio(absoluteMediaUrl(line.audio_url));
      audioRef.current = audio;
      audio.onended = advance;
      audio.onerror = () => {
        const wc = line.text.split(/\s+/).length;
        const ms = Math.max(3500, Math.min(14000, wc * 360));
        timer = setTimeout(advance, ms + (line.pause_after_ms ?? 1500));
        timersRef.current.push(timer);
      };
      audio.play().catch(() => {
        const wc = line.text.split(/\s+/).length;
        const ms = Math.max(3500, Math.min(14000, wc * 360));
        timer = setTimeout(advance, ms + (line.pause_after_ms ?? 1500));
        timersRef.current.push(timer);
      });
    } else {
      const wc = line.text.split(/\s+/).length;
      const ms = Math.max(3500, Math.min(14000, wc * 360));
      const pause = line.pause_after_ms ?? 1500;
      timer = setTimeout(advance, ms + pause);
      timersRef.current.push(timer);
    }
    return () => {
      if (timer) clearTimeout(timer);
      if (audio) { try { audio.pause(); } catch {/* ignore */} }
    };
  }, [phase, lineIndex, lines, muted]);

  const enterPlatform = () => {
    markCinematicSeen();
    if (onComplete) onComplete();
    else navigate('/welcome');
  };

  const skip = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    try { audioRef.current?.pause(); } catch {/* ignore */}
    // Mark seen the moment Skip is pressed so reloads bypass the
    // cinematic. (The CTA `Press the globe to continue` already marks
    // seen; this matches that behavior for the keyboard-skip path.)
    markCinematicSeen();
    setPhase(PHASES.REVEALED);
    setLineIndex(-1);
    setShowCta(true);
  };

  const beginPlayback = () => {
    if (!manifest) return;
    // Deep link: ?moment=globe lands directly on the revealed state.
    let jumpToReveal = false;
    try {
      jumpToReveal = new URLSearchParams(window.location.search).get('moment') === 'globe';
    } catch {/* no window/URL */}
    if (jumpToReveal) {
      setPhase(PHASES.REVEALED);
      setShowCta(true);
      return;
    }
    setPhase(PHASES.PLAYING);
    // Earth fades in over ~2.5s under the bg-black wrapper, then the first
    // narration line starts.
    const t = setTimeout(() => setLineIndex(0), 2400);
    timersRef.current.push(t);
  };

  // ─── Pre-playback gate — JUST THE GLOBE. No play button, no timeline,
  //      no tabs. Click the globe itself to start the intro. Matches
  //      Lead's Apr 27 2026 brief: "you can enlarge [the globe] and
  //      remove the timeline and tabs. Where it is just the globe. And
  //      do the intro from there." Echoes the closing narration line
  //      "Click the globe. And let's begin."
  if (phase === PHASES.TAP) {
    return (
      <div
        className="fixed inset-0 overflow-hidden bg-black"
        data-testid="cinematic-tap-to-begin"
      >
        {/* GLOBE — full-bleed background. Apr 30 2026 (Lead's call):
            globe must be big again, click ANYWHERE on it must fire,
            text is LEFT-aligned in the negative space so the eye stays
            on the planet. */}
        <div
          className="absolute inset-0 cursor-pointer cinematic-fade-in"
          onClick={beginPlayback}
          role="button"
          aria-label="Click the globe to begin"
          data-testid="cinematic-globe-click"
        >
          <PopulatedGlobe testId="cinematic-globe-pre" autoRotate={false} showMoon={false} />
        </div>

        {/* Eyebrow — top-left, anchored, doesn't compete with the globe */}
        <div className="absolute top-5 left-6 md:left-10 z-40 pointer-events-none">
          <div className="font-mono text-[10px] md:text-xs text-white/55 tracking-[0.3em] uppercase">
            The Shared Origins Global Network
          </div>
          <div className="font-historical text-xl md:text-2xl text-[#D4A84A] font-light tracking-tight mt-1">
            TSOGN
          </div>
        </div>

        {/* TEXT ZONE — left-anchored vertical strip. Lead: "Put text to
            the left and stop taking focus away from the globe." Max-width
            keeps the lines tight so the planet stays the hero. */}
        <div
          className="absolute left-6 md:left-12 lg:left-16 bottom-[10vh] md:bottom-[14vh] z-40 max-w-sm pointer-events-none cinematic-fade-in"
          style={{ animationDelay: '1.4s', animationFillMode: 'both', opacity: 0 }}
        >
          <p className="font-mono text-[10px] md:text-xs text-[#D4A84A]/75 tracking-[0.3em] uppercase mb-2">
            Begin where humans began
          </p>
          <p className="font-historical text-3xl md:text-5xl text-white font-light leading-[1.05] mb-3">
            Click the globe.
          </p>
          <p className="font-mono text-[10px] md:text-xs text-white/45 tracking-[0.22em] uppercase leading-relaxed">
            {audioReady ? 'Headphones recommended · 3 minutes · skip anytime' : 'Captions only while the voice bakes · tap to enter'}
          </p>
        </div>

        {/* Skip link — tiny, bottom-right, so curious judges can bypass
            without hunting. No visual weight. */}
        <button
          onClick={(e) => { e.stopPropagation(); enterPlatform(); }}
          data-testid="cinematic-tap-skip"
          className="absolute bottom-4 right-5 z-50 font-mono text-[11px] md:text-xs tracking-widest text-white/45 hover:text-white/90 transition-colors px-3 py-2 min-h-[40px]"
        >
          Skip the intro →
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-hidden bg-black"
      data-testid="cinematic-playing"
    >
      {/* Skip + mute */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        <button
          onClick={() => setMuted((m) => !m)}
          data-testid="cinematic-mute-toggle"
          className="text-white/55 hover:text-white/95 transition-colors p-3 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button
          onClick={skip}
          data-testid="cinematic-skip-btn"
          className="font-mono text-xs md:text-sm tracking-widest text-white/55 hover:text-white/95 transition-colors px-4 py-2.5 min-h-[44px] inline-flex items-center gap-2"
        >
          <SkipForward size={13} /> SKIP
        </button>
      </div>

      {/* GLOBE — full-bleed. Click anywhere fires enterPlatform when the
          CTA is showing. During PLAYING the globe is non-interactive
          (audio is in flight); during REVEALED the whole planet is the
          button per Lead's brief: "click the globe to continue". */}
      <div
        className={`absolute inset-0 cinematic-fade-in ${phase === PHASES.REVEALED && showCta ? 'cursor-pointer' : ''}`}
        onClick={phase === PHASES.REVEALED && showCta ? enterPlatform : undefined}
        role={phase === PHASES.REVEALED && showCta ? 'button' : undefined}
        aria-label={phase === PHASES.REVEALED && showCta ? 'Enter the platform' : undefined}
        data-testid="cinematic-globe-reveal"
      >
        <PopulatedGlobe testId="cinematic-globe" autoRotate={false} showMoon={false} />
      </div>

      {/* PLAYING-phase voiceover caption — anchored bottom-left strip so the
          globe keeps center stage. One sentence at a time, fades cleanly,
          no background fill. Apr 30 2026 iter_61 per Lead: the word
          "subtitle" in the class name + testid was triggering Chrome's
          built-in captions accessibility overlay → rendered a grey box
          behind the text on desktops where captions were enabled. Renamed
          to "speaking" so the UA sheet no longer targets it. */}
      {phase === PHASES.PLAYING && visibleSubline && (
        <div
          className="absolute left-6 md:left-12 lg:left-16 bottom-[12vh] md:bottom-[16vh] z-30 max-w-md md:max-w-lg pointer-events-none"
          data-testid="cinematic-speaking-zone"
          style={{ background: 'transparent', forcedColorAdjust: 'none' }}
        >
          <p
            key={`sub-${lineIndex}-${sublineIdx}`}
            className="cinematic-speaking-fade font-historical text-xl md:text-3xl lg:text-4xl text-white font-light leading-[1.15] tracking-tight"
            data-testid="cinematic-speaking-line"
            style={{ background: 'transparent', forcedColorAdjust: 'none' }}
          >
            {visibleSubline}
          </p>
        </div>
      )}

      {/* Closing CTA — left-anchored. Welcome line + cyan press-globe
          link. No background. No share row. The whole globe behind is
          also a click target so the user can press the planet OR the
          link. */}
      {phase === PHASES.REVEALED && showCta && (
        <div className="absolute left-6 md:left-12 lg:left-16 bottom-[10vh] md:bottom-[14vh] z-30 max-w-md md:max-w-lg pointer-events-auto">
          <p className="font-mono text-[10px] md:text-xs text-[#D4A84A]/75 tracking-[0.3em] uppercase mb-2 cinematic-fade-in">
            Out of Africa · Into Everywhere
          </p>
          <p className="cinematic-fade-in font-historical text-3xl md:text-5xl text-white font-light leading-[1.05] mb-4">
            Welcome to TSOGN.
            <span className="block text-white/70 text-2xl md:text-4xl mt-1">
              We have been waiting for you.
            </span>
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); enterPlatform(); }}
            data-testid="cinematic-globe-cta"
            className="cinematic-fade-in inline-flex items-center gap-2 font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-[#2F8A92]/95 hover:text-[#D4A84A] transition-colors px-2 py-2 min-h-[44px] border-b border-[#2F8A92]/30 hover:border-[#D4A84A]"
          >
            Press the globe to continue →
          </button>
        </div>
      )}
    </div>
  );
}
